// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaStethoscope,
  FaBrain,
  FaHospital,
  FaShieldAlt,
  FaPills,
  FaLayerGroup,
  FaRobot,
  FaHome,
  FaChevronDown,
} from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";
import { HiDocumentText } from "react-icons/hi";
import { MdMic, MdPublic } from "react-icons/md";

const CALENDLY_URL = "https://calendly.com/khushboo-dasot-carer/30min";

const PRODUCTS = [
  { href: "/product/voiz", icon: <MdMic size={16} />, label: "CarerVoiz" },
  { href: "/product/kms", icon: <FaBrain size={16} />, label: "CarerKMS" },
  { href: "/hospilot", icon: <FaHospital size={16} />, label: "Hospilot" },
];

const WHO_WE_SERVE = [
  { href: "/verticals/provider", icon: <FaStethoscope size={16} />, label: "Provider" },
  { href: "/verticals/payer", icon: <FaShieldAlt size={16} />, label: "Payer" },
  { href: "/verticals/pharma", icon: <FaPills size={16} />, label: "Pharma" },
  { href: "/product/publichealth", icon: <MdPublic size={16} />, label: "Public Health" },
];

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<"products" | "whoWeServe" | null>(null);
  const [openDropdown, setOpenDropdown] = useState<"products" | "whoWeServe" | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [onWhiteBg, setOnWhiteBg] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 24);

      // Nearly every page on the site is light-background, so default to
      // readable dark nav text and only special-case the handful of pages
      // that are actually dark: the homepage, individual vertical pages (but
      // not /verticals/payer, which is light), and individual agent detail
      // pages (but not /agents itself, which is light). Resources/blog is
      // light-themed now, so it no longer needs the dark-nav special case.
      const isDarkPage =
        (location.pathname.startsWith('/verticals/') &&
          location.pathname !== '/verticals/payer' &&
          location.pathname !== '/verticals/provider' &&
          location.pathname !== '/verticals/pharma') ||
        location.pathname.startsWith('/agents/');
      setOnWhiteBg(!isDarkPage);
    };

    handler(); // Call immediately
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [location]);

  // Close any open desktop dropdown on outside click, and on route change.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setIsMenuOpen(false);
    setMobileSection(null);
  }, [location.pathname, location.hash]);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const bookDemo = () => window.open(CALENDLY_URL, "_blank");

  const linkTextClass = (isActive: boolean) =>
    isActive
      ? onWhiteBg
        ? "text-blue-600"
        : "text-white"
      : onWhiteBg
      ? "text-gray-700 hover:text-gray-900"
      : "text-gray-400 hover:text-white";

  const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative block px-2.5 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${linkTextClass(
      isActive
    )}`;

  const activeUnderlay = (isActive: boolean) =>
    isActive && (
      <span
        className="absolute inset-0 rounded-lg border"
        style={{
          borderColor: onWhiteBg ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.4)",
          background: onWhiteBg ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.15)",
        }}
      />
    );

  const isGroupActive = (items: { href: string }[]) =>
    items.some((item) => location.pathname.startsWith(item.href));

  const renderDesktopDropdown = (
    key: "products" | "whoWeServe",
    label: string,
    items: { href: string; icon: React.ReactNode; label: string }[]
  ) => {
    const groupActive = isGroupActive(items);
    const isOpen = openDropdown === key;

    return (
      <li
        className="relative"
        onMouseEnter={() => setOpenDropdown(key)}
        onMouseLeave={() => setOpenDropdown((prev) => (prev === key ? null : prev))}
      >
        <button
          type="button"
          onClick={() => setOpenDropdown(key)}
          aria-expanded={isOpen}
          className={`relative flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${linkTextClass(
            groupActive || isOpen
          )}`}
        >
          {activeUnderlay(groupActive || isOpen)}
          <span className="relative">{label}</span>
          <FaChevronDown
            size={9}
            className={`relative transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-56"
            style={{ zIndex: 60 }}
          >
            <div
              className="rounded-xl overflow-hidden p-1.5"
              style={{
                background: onWhiteBg ? "rgba(255,255,255,0.98)" : "rgba(10,14,26,0.98)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: onWhiteBg
                  ? "1px solid rgba(0,0,0,0.08)"
                  : "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.24)",
              }}
            >
              {items.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                      isActive
                        ? onWhiteBg
                          ? "text-blue-600 bg-blue-500/10"
                          : "text-white bg-blue-500/15"
                        : onWhiteBg
                        ? "text-gray-700 hover:bg-black/[0.04] hover:text-gray-900"
                        : "text-gray-400 hover:bg-white/[0.06] hover:text-white"
                    }`
                  }
                >
                  <span className={onWhiteBg ? "text-blue-600" : "text-blue-400"}>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <nav
      ref={navRef}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-7xl transition-all duration-500 rounded-2xl"
      style={{
        background: onWhiteBg
          ? scrolled
            ? "rgba(255,255,255,0.95)"
            : "rgba(255,255,255,0.90)"
          : scrolled
          ? "rgba(255,255,255,0.06)"
          : "rgba(255,255,255,0.02)",
        backdropFilter: "blur(24px) saturate(180%)",
        WebkitBackdropFilter: "blur(24px) saturate(180%)",
        border: onWhiteBg
          ? "1px solid rgba(59,130,246,0.15)"
          : scrolled
          ? "1px solid rgba(255,255,255,0.10)"
          : "1px solid rgba(255,255,255,0.05)",
        boxShadow: onWhiteBg
          ? "0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)"
          : scrolled
          ? "0 4px 32px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)"
          : "none",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">

        {/* Logo — doubles as the link home */}
        <Link to="/" className="flex-shrink-0 group" aria-label="Carer home">
          <img
            src="/carer.png"
            alt="Carer logo"
            className="h-9 cursor-pointer transition-opacity duration-200 group-hover:opacity-80"
          />
        </Link>

        {/* Desktop nav — centered */}
        <ul className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          <li>
            <NavLink to="/" end className={desktopLinkClass}>
              {({ isActive }) => (
                <>
                  {activeUnderlay(isActive)}
                  <span className="relative">Home</span>
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink to="/platform" className={desktopLinkClass}>
              {({ isActive }) => (
                <>
                  {activeUnderlay(isActive)}
                  <span className="relative">Platform</span>
                </>
              )}
            </NavLink>
          </li>

          {renderDesktopDropdown("products", "Products", PRODUCTS)}
          {renderDesktopDropdown("whoWeServe", "Who We Serve", WHO_WE_SERVE)}

          <li>
            <Link
              to="/agents"
              className={`relative block px-2.5 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${linkTextClass(
                location.pathname === "/agents"
              )}`}
            >
              Agents
            </Link>
          </li>
          <li>
            <NavLink to="/resources" className={desktopLinkClass}>
              {({ isActive }) => (
                <>
                  {activeUnderlay(isActive)}
                  <span className="relative">Resources</span>
                </>
              )}
            </NavLink>
          </li>
        </ul>

        {/* Book a Demo — mirrors the logo on the right */}
        <button
          onClick={bookDemo}
          className="hidden lg:inline-flex items-center gap-1.5 flex-shrink-0 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90 transition-opacity duration-200 shadow-md whitespace-nowrap"
        >
          Book a Demo
          <FiArrowRight size={14} />
        </button>

        {/* Hamburger */}
        <button
          className={`lg:hidden p-2 rounded-xl transition-all duration-200 focus:outline-none ${
            onWhiteBg
              ? "text-gray-700 hover:text-gray-900 bg-gray-100"
              : "text-gray-400 hover:text-white"
          }`}
          style={{ background: onWhiteBg ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)" }}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div
          className="lg:hidden px-4 pb-4 rounded-b-2xl max-h-[75vh] overflow-y-auto"
          style={{
            background: onWhiteBg ? "rgba(255,255,255,0.97)" : "rgba(8,12,24,0.92)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderTop: onWhiteBg
              ? "1px solid rgba(0,0,0,0.07)"
              : "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <div className="pt-3 space-y-0.5">
            <NavLink
              to="/"
              end
              onClick={toggleMenu}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? onWhiteBg
                      ? "text-blue-600 bg-blue-500/10 border border-blue-500/30"
                      : "text-white bg-blue-500/20 border border-blue-500/40"
                    : onWhiteBg
                    ? "text-gray-700 hover:text-gray-900"
                    : "text-gray-400 hover:text-white"
                }`
              }
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                <FaHome size={16} />
              </span>
              Home
            </NavLink>

            <NavLink
              to="/platform"
              onClick={toggleMenu}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? onWhiteBg
                      ? "text-blue-600 bg-blue-500/10 border border-blue-500/30"
                      : "text-white bg-blue-500/20 border border-blue-500/40"
                    : onWhiteBg
                    ? "text-gray-700 hover:text-gray-900"
                    : "text-gray-400 hover:text-white"
                }`
              }
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                <FaLayerGroup size={16} />
              </span>
              Platform
            </NavLink>

            {/* Products — accordion */}
            <button
              type="button"
              onClick={() => setMobileSection((prev) => (prev === "products" ? null : "products"))}
              className={`w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isGroupActive(PRODUCTS)
                  ? onWhiteBg
                    ? "text-blue-600"
                    : "text-white"
                  : onWhiteBg
                  ? "text-gray-700 hover:text-gray-900"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-2.5">
                <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                  <FaBrain size={16} />
                </span>
                Products
              </span>
              <FaChevronDown
                size={11}
                className={`transition-transform duration-200 ${mobileSection === "products" ? "rotate-180" : ""}`}
              />
            </button>
            {mobileSection === "products" && (
              <div className="pl-4 space-y-0.5">
                {PRODUCTS.map(({ href, icon, label }) => (
                  <NavLink
                    key={label}
                    to={href}
                    onClick={toggleMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? onWhiteBg
                            ? "text-blue-600 bg-blue-500/10 border border-blue-500/30"
                            : "text-white bg-blue-500/20 border border-blue-500/40"
                          : onWhiteBg
                          ? "text-gray-600 hover:text-gray-900"
                          : "text-gray-500 hover:text-white"
                      }`
                    }
                  >
                    <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                      {icon}
                    </span>
                    {label}
                  </NavLink>
                ))}
              </div>
            )}

            {/* Who We Serve — accordion */}
            <button
              type="button"
              onClick={() => setMobileSection((prev) => (prev === "whoWeServe" ? null : "whoWeServe"))}
              className={`w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isGroupActive(WHO_WE_SERVE)
                  ? onWhiteBg
                    ? "text-blue-600"
                    : "text-white"
                  : onWhiteBg
                  ? "text-gray-700 hover:text-gray-900"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-2.5">
                <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                  <FaStethoscope size={16} />
                </span>
                Who We Serve
              </span>
              <FaChevronDown
                size={11}
                className={`transition-transform duration-200 ${mobileSection === "whoWeServe" ? "rotate-180" : ""}`}
              />
            </button>
            {mobileSection === "whoWeServe" && (
              <div className="pl-4 space-y-0.5">
                {WHO_WE_SERVE.map(({ href, icon, label }) => (
                  <NavLink
                    key={label}
                    to={href}
                    onClick={toggleMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? onWhiteBg
                            ? "text-blue-600 bg-blue-500/10 border border-blue-500/30"
                            : "text-white bg-blue-500/20 border border-blue-500/40"
                          : onWhiteBg
                          ? "text-gray-600 hover:text-gray-900"
                          : "text-gray-500 hover:text-white"
                      }`
                    }
                  >
                    <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                      {icon}
                    </span>
                    {label}
                  </NavLink>
                ))}
              </div>
            )}

            <Link
              to="/agents"
              onClick={toggleMenu}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                onWhiteBg ? "text-gray-700 hover:text-gray-900" : "text-gray-400 hover:text-white"
              }`}
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                <FaRobot size={16} />
              </span>
              Agents
            </Link>

            <NavLink
              to="/resources"
              onClick={toggleMenu}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? onWhiteBg
                      ? "text-blue-600 bg-blue-500/10 border border-blue-500/30"
                      : "text-white bg-blue-500/20 border border-blue-500/40"
                    : onWhiteBg
                    ? "text-gray-700 hover:text-gray-900"
                    : "text-gray-400 hover:text-white"
                }`
              }
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${onWhiteBg ? "text-blue-600" : "text-blue-400"}`}>
                <HiDocumentText size={16} />
              </span>
              Resources
            </NavLink>

            <button
              onClick={() => {
                toggleMenu();
                bookDemo();
              }}
              className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90 transition-opacity duration-200 shadow-md"
            >
              Book a Demo
              <FiArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
